import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, CardMedia, Box, Button, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';
import { getParticipants, verifySession } from './api';

const ROLES = ['Table Topic', 'Speaker', 'Evaluator'];

interface ResultsPageProps {
  session: string;
}

const ResultsPage: React.FC<ResultsPageProps> = ({ session }) => {
  useEffect(() => {
    document.title = 'ACE The Place Toastmasters Club - Results';
  }, []);

  // Get view type from URL params (default: 'winner')
  const [viewType, setViewType] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    return view === 'top3' ? 'top3' : 'winner';
  });

  const [authenticated, setAuthenticated] = useState(() => {
    return localStorage.getItem(`resultsAuthenticated_${session}`) === 'true';
  });
  const [inputPassword, setInputPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [revealedCards, setRevealedCards] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchAllParticipants = async () => {
      try {
        let all: any[] = [];
        for (const role of ROLES) {
          const res = await getParticipants(session, role);
          all = all.concat(res.data);
        }
        setParticipants(all);
      } catch (error) {
        console.error('Failed to fetch participants:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authenticated) {
      fetchAllParticipants();
    }
  }, [session, authenticated]);

  const getWinnersByRole = (role: string) => {
    const filtered = participants.filter(p => p.role === role);
    if (!filtered.length) return null;
    
    const maxVotes = Math.max(...filtered.map(p => p.votes));
    const winners = filtered.filter(p => p.votes === maxVotes);
    const isTie = winners.length > 1;
    const totalVotes = filtered.reduce((sum, p) => sum + p.votes, 0);
    
    return { winners, isTie, maxVotes, totalVotes };
  };

  const getTop3ByRole = (role: string) => {
    const filtered = participants.filter(p => p.role === role);
    if (!filtered.length) return null;
    
    // Sort by votes in descending order and take top 3
    const sorted = filtered.sort((a, b) => b.votes - a.votes);
    const top3 = sorted.slice(0, 3);
    const totalVotes = filtered.reduce((sum, p) => sum + p.votes, 0);
    
    return { top3, totalVotes };
  };

  // Update URL when view type changes
  const updateViewType = (newViewType: 'winner' | 'top3') => {
    setViewType(newViewType);
    const url = new URL(window.location.href);
    if (newViewType === 'top3') {
      url.searchParams.set('view', 'top3');
    } else {
      url.searchParams.delete('view');
    }
    window.history.replaceState({}, '', url.toString());
  };

  const handleCardClick = (role: string) => {
    setRevealedCards(prev => new Set([...prev, role]));
  };

  const isCardRevealed = (role: string) => {
    return revealedCards.has(role);
  };

  if (!authenticated) {
    return (
      <Container disableGutters maxWidth={false} sx={{ minHeight: '100vh', position: 'relative', bgcolor: '#f8f6f1', backgroundImage: 'url(/tile_background.png)', backgroundRepeat: 'repeat' }}>
        <Box sx={{ mb: { xs: 1, sm: 2 }, pt: { xs: 2, sm: 4 }, pb: { xs: 2, sm: 4 }, position: 'relative', width: '100%' }}>
          <Typography variant="h4" align="left" sx={{ position: 'relative', zIndex: 1, fontWeight: 250, color: '#111', letterSpacing: 1, fontSize: { xs: '2.3rem', sm: '2.8rem' }, lineHeight: 1.1, fontFamily: 'Montserrat, Arial, sans-serif', background: 'transparent', px: { xs: 3, sm: 4 } }}>
            Results Access
          </Typography>
        </Box>
        <Container maxWidth="sm" sx={{ mt: 2 }}>
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', borderTop: '6px solid #772432', borderRadius: 0, bgcolor: '#fff' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, letterSpacing: 1, color: '#111', fontFamily: 'Montserrat, Arial, sans-serif' }}>Results Login</Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>Enter the session password to view voting results.</Typography>
            <Box component="form" onSubmit={async (e) => {
              e.preventDefault();
              setIsAuthenticating(true);
              setAuthError('');
              
              try {
                const result = await verifySession(session, inputPassword);
                if (result.error) {
                  setAuthError(result.error);
                } else {
                  setAuthenticated(true);
                  localStorage.setItem(`resultsAuthenticated_${session}`, 'true');
                  setInputPassword('');
                }
              } catch (error) {
                setAuthError('Failed to verify password. Please try again.');
              } finally {
                setIsAuthenticating(false);
              }
            }} sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <input
                type="password"
                value={inputPassword}
                onChange={e => setInputPassword(e.target.value)}
                placeholder="Session Password"
                style={{ padding: 12, fontSize: 18, width: '100%', maxWidth: 320, border: '1px solid #d7d0c6', borderRadius: 2, boxSizing: 'border-box' }}
                required
                disabled={isAuthenticating}
              />
              {authError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {authError}
                </Typography>
              )}
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isAuthenticating}
                sx={{ minWidth: 140, py: 1.2, bgcolor: '#772432', '&:hover': { bgcolor: '#5f1c28' } }}
              >
                {isAuthenticating ? 'Verifying...' : 'Login'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth={false} sx={{ minHeight: '100vh', position: 'relative', bgcolor: '#f8f6f1', backgroundImage: 'url(/tile_background.png)', backgroundRepeat: 'repeat' }}>
        <Box sx={{ mb: { xs: 1, sm: 2 }, pt: { xs: 2, sm: 4 }, pb: { xs: 2, sm: 4 }, position: 'relative', width: '100%' }}>
          <Typography variant="h4" align="left" sx={{ position: 'relative', zIndex: 1, fontWeight: 250, color: '#111', letterSpacing: 1, fontSize: { xs: '2.3rem', sm: '2.8rem' }, lineHeight: 1.1, fontFamily: 'Montserrat, Arial, sans-serif', background: 'transparent', px: { xs: 3, sm: 4 } }}>
            Loading Results...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
            }
            50% {
              opacity: 1;
            }
          }
        `}
      </style>
      <Container disableGutters maxWidth={false} sx={{ minHeight: '100vh', position: 'relative', bgcolor: '#f8f6f1', backgroundImage: 'url(/tile_background.png)', backgroundRepeat: 'repeat' }}>
      <Box sx={{ mb: { xs: 1, sm: 2 }, pt: { xs: 2, sm: 4 }, pb: { xs: 2, sm: 4 }, position: 'relative', width: '100%' }}>
        <Typography variant="h4" align="left" sx={{ position: 'relative', zIndex: 1, fontWeight: 250, color: '#111', letterSpacing: 1, fontSize: { xs: '2.3rem', sm: '2.8rem' }, lineHeight: 1.1, fontFamily: 'Montserrat, Arial, sans-serif', background: 'transparent', px: { xs: 3, sm: 4 }, pt: {xs:5, sm: 5} }}>
          Voting Results
        </Typography>
      </Box>
      <Container sx={{
        width: '100%',
        pt: { xs: 2, sm: 4 },
        pb: { xs: 16, sm: 0 },
        px: { xs: 2, sm: 4 },
        minHeight: 'calc(100vh - 120px)',
        mb: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        zIndex: 1
      }}>
        
        {/* View Toggle Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, gap: 2 }}>
          <Button
            variant={viewType === 'winner' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => updateViewType('winner')}
            sx={{ 
              px: 3, 
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}
          >
            🏆 Winners Only
          </Button>
          <Button
            variant={viewType === 'top3' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => updateViewType('top3')}
            sx={{ 
              px: 3, 
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}
          >
            🥇🥈🥉 Top 3
          </Button>
        </Box>
        
        <Grid container spacing={3} justifyContent="center">
          {ROLES.map(role => {
            const winnerResult = getWinnersByRole(role);
            const top3Result = getTop3ByRole(role);
            
            if (!winnerResult && !top3Result) return null;
            
            const currentResult = viewType === 'top3' ? top3Result : winnerResult;
            const isWinnerView = viewType === 'winner';
            const participants = isWinnerView ? winnerResult?.winners : top3Result?.top3;
            const isTie = isWinnerView ? winnerResult?.isTie : false;
            const maxVotes = isWinnerView ? winnerResult?.maxVotes : 0;
            const totalVotes = currentResult?.totalVotes || 0;
            
            return (
              <Grid size={{ xs: 12, sm: 10, md: 8 }} key={role}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 0,
                    px: 0,
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: 500,
                    background: '#fff',
                    transition: 'all 0.5s ease-in-out',
                    mx: 'auto',
                    overflow: 'hidden',
                    cursor: isCardRevealed(role) ? 'default' : 'pointer',
                    position: 'relative',
                    '&:hover': isCardRevealed(role) ? {
                      transform: 'translateY(-2px)',
                    } : {
                      transform: 'scale(1.02)',
                    }
                  }}
                  onClick={() => !isCardRevealed(role) && handleCardClick(role)}
                >
                  {/* Card Cover */}
                  {!isCardRevealed(role) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        height: '100%',
                        background: '#772432',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        transition: 'all 0.5s ease-in-out',
                        color: 'white',
                        textAlign: 'center',
                        px: 3
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700, 
                          mb: 2,
                          fontSize: { xs: '2rem', sm: '2.5rem' }
                        }}
                      >
                        {viewType === 'top3' ? '🥇🥈🥉' : '🏆'}
                      </Typography>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1,
                          fontSize: { xs: '1.3rem', sm: '1.5rem' }
                        }}
                      >
                        {viewType === 'top3' ? `Top 3 ${role}` : `Best ${role}`}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          opacity: 0.9,
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}
                      >
                        Click to reveal the {viewType === 'top3' ? 'rankings!' : 'winner!'}
                      </Typography>
                      <Box
                        sx={{
                          mt: 2,
                          width: 40,
                          height: 2,
                          bgcolor: 'rgba(255,255,255,0.6)',
                          borderRadius: 1,
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Card Content */}
                  <Box sx={{ 
                    width: '100%', 
                    bgcolor: '#772432', 
                    py: 2, 
                    textAlign: 'center',
                    opacity: isCardRevealed(role) ? 1 : 0,
                    transform: isCardRevealed(role) ? 'translateY(0)' : 'translateY(-20px)',
                    transition: 'all 0.6s ease-in-out 0.2s'
                  }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                      Best {role}{isTie ? 's' : ''}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: viewType === 'top3' ? 'column' : 'row', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: viewType === 'top3' ? 1 : 2, 
                    py: 3, 
                    px: 2,
                    width: '100%',
                    opacity: isCardRevealed(role) ? 1 : 0,
                    transform: isCardRevealed(role) ? 'scale(1)' : 'scale(0.8)',
                    transition: 'all 0.6s ease-in-out 0.4s'
                  }}>
                    {participants?.map((participant: any, index: number) => participant.imageUrl && (
                      <Box 
                        key={participant._id} 
                        sx={{ 
                          display: 'flex',
                          flexDirection: viewType === 'top3' ? 'row' : 'column',
                          alignItems: 'center',
                          gap: viewType === 'top3' ? 2 : 0,
                          width: viewType === 'top3' ? '100%' : 'auto',
                          justifyContent: viewType === 'top3' ? 'flex-start' : 'center',
                          opacity: isCardRevealed(role) ? 1 : 0,
                          transform: isCardRevealed(role) ? 'translateY(0)' : 'translateY(30px)',
                          transition: `all 0.5s ease-in-out ${0.6 + index * 0.2}s`,
                          pl: viewType === 'top3' ? 2 : 0
                        }}
                      >
                        {viewType === 'top3' && (
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              minWidth: 60,
                              textAlign: 'center',
                              fontSize: { xs: '1.5rem', sm: '2rem' }
                            }}
                          >
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                          </Typography>
                        )}
                        <CardMedia
                          component="img"
                          image={`${process.env.REACT_APP_API_URL}/api/${session}/image/${participant.imageUrl.split('/').pop()}`}
                          alt={participant.name}
                          sx={{
                            objectFit: 'cover',
                            width: viewType === 'top3' ? 80 : 120,
                            height: viewType === 'top3' ? 80 : 120,
                            borderRadius: '50%',
                            border: `4px solid ${viewType === 'top3' ? 
                              (index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32') : 
                              '#1976d2'}`,
                            animation: isCardRevealed(role) ? 'bounce 0.6s ease-in-out' : 'none',
                            animationDelay: `${0.8 + index * 0.2}s`,
                            '@keyframes bounce': {
                              '0%, 20%, 50%, 80%, 100%': {
                                transform: 'translateY(0)'
                              },
                              '40%': {
                                transform: 'translateY(-10px)'
                              },
                              '60%': {
                                transform: 'translateY(-5px)'
                              }
                            }
                          }}
                        />
                        <Box sx={{ 
                          textAlign: viewType === 'top3' ? 'left' : 'center',
                          flex: viewType === 'top3' ? 1 : 'none'
                        }}>
                          <Typography 
                            variant={viewType === 'top3' ? 'h6' : 'h6'} 
                            sx={{ 
                              mt: viewType === 'top3' ? 0 : 1,
                              fontWeight: 600, 
                              color: '#772432',
                              opacity: isCardRevealed(role) ? 1 : 0,
                              transition: `opacity 0.4s ease-in-out ${0.9 + index * 0.2}s`,
                              fontSize: viewType === 'top3' ? { xs: '1rem', sm: '1.1rem' } : { xs: '1.1rem', sm: '1.25rem' }
                            }}
                          >
                            {participant.name}
                          </Typography>
                          {viewType === 'top3' && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#666',
                                fontWeight: 500
                              }}
                            >
                              {participant.votes} votes ({totalVotes > 0 ? ((participant.votes / totalVotes) * 100).toFixed(1) : 0}%)
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  
                  <Box sx={{ 
                    textAlign: 'center', 
                    px: 3, 
                    pb: 3, 
                    width: '100%',
                    opacity: isCardRevealed(role) ? 1 : 0,
                    transform: isCardRevealed(role) ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-in-out 0.8s'
                  }}>
                    {viewType === 'winner' ? (
                      <>
                        <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
                          Votes: {maxVotes} / Total: {totalVotes}
                        </Typography>
                        {totalVotes > 0 && participants && (
                          <Typography variant="body2" sx={{ color: '#888' }}>
                            (
                            {participants.map((w: any, i: number) => (
                              <span key={w._id}>
                                {((w.votes / totalVotes) * 100).toFixed(1)}%
                                {i < participants.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                            )
                          </Typography>
                        )}
                        {isTie && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#d32f2f', 
                              fontWeight: 600, 
                              mt: 1,
                              opacity: isCardRevealed(role) ? 1 : 0,
                              transition: 'opacity 0.4s ease-in-out 1s'
                            }}
                          >
                            🎯 Tie Result
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
                        Total Votes: {totalVotes}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic' }}>
            💡 URL Shortcuts: Add <code>?view=top3</code> to see top 3 rankings
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.history.back()}
            sx={{ px: 4, py: 1.5, borderRadius: 2, mr: 2 }}
          >
            Back to Admin
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => {
              setAuthenticated(false);
              localStorage.removeItem(`resultsAuthenticated_${session}`);
            }}
            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
          >
            Logout
          </Button>
        </Box>
      </Container>
    </Container>
    </>
  );
};

export default ResultsPage; 